import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  aiSendFollowUpQuestionForDataExplorerStreamed,
  aiCloseSidebar,
  aiToggleSideBar,
} from "../../store/actions/dashboard/dashboard";
import { useRouteMatch } from "react-router-dom";
import { useHistory } from "react-router";
import AISidebar from "./AISidebar";
import useApiQuery from "../../hooks/useApiQuery";
import { listAiPrompts } from "../../api/aiPrompts";

export function useAiSidebarStateObject() {
  const chartImageState = useState(null);
  const questionState = useState("");
  const widthState = useState(500);

  return { chartImageState, questionState, widthState };
}

export function useDataExplorerAiSidebar() {
  const dispatch = useDispatch();
  const aiSidebar = useSelector((state) => state.dashboard.aiSidebar ?? {});
  const aiSidebarStateObject = useAiSidebarStateObject();
  const aiLoading = aiSidebar.requestInFlight || aiSidebar.loading;
  const [question, setQuestion] = useState("");

  const controllerRef = useRef(new AbortController());

  useEffect(() => {
    return () => {
      controllerRef.current.abort();
      dispatch(aiCloseSidebar());
    };
  }, [dispatch]);

  const onSendFollowUpQuestion = async (question) => {
    controllerRef.current = new AbortController();
    const prompts = promptsQuery.data ?? (await promptsQuery.refetchData());
    const prompt = prompts.find((v) => v.key === "data-explorer");
    dispatch(
      aiSendFollowUpQuestionForDataExplorerStreamed(
        question,
        controllerRef.current.signal,
        { systemPromptOverride: prompt?.promptMessage }
      )
    );
  };

  const onStop = () => {
    controllerRef.current.abort();
    controllerRef.current = new AbortController();
  };
  const promptsQuery = useApiQuery(listAiPrompts, { enabled: false });
  const routeMatch = useRouteMatch();
  const history = useHistory();
  useEffect(() => {
    if (aiSidebar.toolResponse) {
      const path = routeMatch.path + "/ai-results";
      history.push(path);
    }
  }, [aiSidebar.toolResponse, history, routeMatch.path]);

  const askAi = (question) => {
    if (aiLoading) return; // ignore extra clicks while loading
    onSendFollowUpQuestion(question);
  };

  const toggleSideBar = () => {
    const toggle = history.location.pathname.includes("/ai-results");
    if (toggle) dispatch(aiToggleSideBar(false));
    else dispatch(aiCloseSidebar());
  };

  return {
    aiSidebarStateObject,
    askAi,
    aiSidebarNode:
      aiSidebar.open || aiSidebar.loading ? (
        <AISidebar
          isOpen={aiSidebar.open}
          data={aiSidebar.content}
          initialQuestion={aiSidebar.initialQuestion}
          predefinedQuestion={aiSidebarStateObject.questionState?.[0]}
          loading={aiSidebar.loading}
          title={aiSidebar.title}
          onClose={toggleSideBar}
          onSendFollowUpQuestion={onSendFollowUpQuestion}
          onStop={onStop}
          liftedStateObject={{
            questionState: [question, setQuestion],
            chartImageState: [],
            widthState: [],
          }}
        />
      ) : null,
  };
}

export function getIsGenerating(
  aiSidebar,
  powerEditorMode,
  ignoreConversationId
) {
  const { conversationId, followUpLoading, messages } = aiSidebar ?? {};

  if (powerEditorMode) return !!followUpLoading;

  if (followUpLoading) {
    return true;
  }

  // Only treat missing conversationId as generating while actively loading.
  // After an abort before conversationId arrives, followUpLoading is false
  // so we should NOT be stuck in generating state.
  if (!conversationId && !ignoreConversationId) {
    return false;
  }

  return (
    Array.isArray(messages) &&
    messages.some((m) => m?.type === "ai" && !m?.content)
  );
}
